import React, { useState, useEffect } from 'react';
import * as api from '../../utils/api';
import styles from './SupportPage.module.css';
import { FaPlus, FaHistory, FaTicketAlt, FaCloudUploadAlt, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import MessageBox from '../../components/MessageBox';
import { useAuth } from '../../context/AuthContext';

function SupportPage() {
  const [tickets, setTickets] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [subject, setSubject] = useState('');
  const [messageText, setMessageText] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [alertMessage, setAlertMessage] = useState(null);
  const [expandedTicket, setExpandedTicket] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const { user } = useAuth();

  const fetchTickets = async () => {
    try {
      const { data } = await api.getMySupportTickets();
      setTickets(data);
    } catch (err) {
      console.error("Error fetching tickets:", err);
    } finally {
      setFetchLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, []);

  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!subject || !messageText) {
      return setAlertMessage({ type: 'error', text: 'Please fill in all fields.' });
    }

    setLoading(true);
    try {
      const { data: newTicket } = await api.createSupportTicket({
        subject,
        message: messageText,
        name: user.name,
      });

      if (selectedFile) {
        const formData = new FormData();
        formData.append('screenshot', selectedFile);
        await api.uploadSupportScreenshot(newTicket._id, formData);
      }

      setAlertMessage({ type: 'success', text: 'Support ticket raised successfully!' });
      setSubject('');
      setMessageText('');
      setSelectedFile(null);
      setShowForm(false);
      fetchTickets();
    } catch (err) {
      setAlertMessage({ type: 'error', text: 'Failed to raise ticket. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (id) => {
    setExpandedTicket(expandedTicket === id ? null : id);
  };

  return (
    <div className={styles['support-page']}>
      {alertMessage && <MessageBox message={alertMessage.text} type={alertMessage.type} onClose={() => setAlertMessage(null)} />}
      
      <div className={styles['support-header']}>
        <h1>Help & Support</h1>
        <p>Need help? Raise a ticket and our team will get back to you.</p>
      </div>

      <div className={styles['actions-bar']}>
        <button 
          className={`${styles['toggle-btn']} ${showForm ? styles.active : ''}`}
          onClick={() => setShowForm(!showForm)}
        >
          {showForm ? <FaHistory /> : <FaPlus />}
          <span>{showForm ? 'View My Tickets' : 'Raise New Ticket'}</span>
        </button>
      </div>

      {showForm ? (
        <div className={styles['form-card']}>
          <form onSubmit={handleSubmit} className={styles['ticket-form']}>
            <div className={styles['form-group']}>
              <label>Subject</label>
              <input 
                type="text" 
                placeholder="e.g., Deposit Issue, KYC Verification" 
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </div>
            <div className={styles['form-group']}>
              <label>Message</label>
              <textarea 
                placeholder="Describe your problem in detail..." 
                rows="5"
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
              ></textarea>
            </div>
            <div className={styles['form-group']}>
              <label>Attachment (Optional)</label>
              <div className={styles['file-upload-wrapper']}>
                <input type="file" id="screenshot" onChange={handleFileChange} accept="image/*" hidden />
                <label htmlFor="screenshot" className={styles['file-label']}>
                  <FaCloudUploadAlt />
                  <span>{selectedFile ? selectedFile.name : 'Upload Screenshot'}</span>
                </label>
              </div>
            </div>
            <button type="submit" className={styles['submit-btn']} disabled={loading}>
              {loading ? 'Submitting...' : 'Submit Ticket'}
            </button>
          </form>
        </div>
      ) : (
        <div className={styles['tickets-list']}>
          {fetchLoading ? (
            <div className={styles.loading}>Loading your tickets...</div>
          ) : tickets.length === 0 ? (
            <div className={styles['empty-state']}>
              <FaTicketAlt />
              <p>You haven't raised any tickets yet.</p>
            </div>
          ) : (
            tickets.map(ticket => (
              <div key={ticket._id} className={`${styles['ticket-item']} ${styles[ticket.status.toLowerCase().replace(' ', '')]}`}>
                <div className={styles['ticket-summary']} onClick={() => toggleExpand(ticket._id)}>
                  <div className={styles['ticket-info-left']}>
                    <span className={styles['status-dot']}></span>
                    <div className={styles['ticket-main']}>
                      <h4>{ticket.subject}</h4>
                      <span className={styles['ticket-date']}>{new Date(ticket.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <div className={styles['ticket-info-right']}>
                    <span className={styles['status-text']}>{ticket.status}</span>
                    {expandedTicket === ticket._id ? <FaChevronUp /> : <FaChevronDown />}
                  </div>
                </div>
                
                {expandedTicket === ticket._id && (
                  <div className={styles['ticket-details']}>
                    <div className={styles['detail-block']}>
                      <label>Your Message:</label>
                      <p>{ticket.message}</p>
                    </div>
                    {ticket.screenshot && (
                      <div className={styles['detail-block']}>
                        <label>Attachment:</label>
                        <button 
                          className={styles['view-screenshot-btn']} 
                          onClick={() => setPreviewImage(ticket.screenshot)}
                        >
                          View Screenshot
                        </button>
                      </div>
                    )}
                    {ticket.adminResponse && (
                      <div className={`${styles['detail-block']} ${styles.response}`}>
                        <label>Official Response:</label>
                        <p>{ticket.adminResponse}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* Image Modal */}
      {previewImage && (
        <div className={styles['image-modal']} onClick={() => setPreviewImage(null)}>
          <div className={styles['modal-content']} onClick={e => e.stopPropagation()}>
            <button className={styles['close-modal']} onClick={() => setPreviewImage(null)}>&times;</button>
            <img src={previewImage} alt="Screenshot Preview" />
          </div>
        </div>
      )}
    </div>
  );
}

export default SupportPage;
